import { EventEmitter, Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { catchError, finalize, map } from 'rxjs/operators';
import { Login } from '../../shared/models/login';
import { AccessTokenResponse } from '../../shared/models/accessTokenResponse';
import { BehaviorSubject, Observable, of, throwError } from 'rxjs';
import { AppConstants } from '../../shared/utils/app-constants';
import { ApiUrlConstants } from '../../shared/utils/api-constants';
import { RoleEnum } from '../../shared/enums/role-enum';
import { AppConfigService } from './app-config.service';
import { User, UserView } from '../../shared/models/user';
import * as moment from 'moment';
import { Moment } from 'moment';
import { Router } from '@angular/router';
import { jwtDecode } from 'jwt-decode';
import { UrlConstants } from '../../shared/utils/url-constants';
import { UsersService } from './users.service';
import { TranslateService } from '@ngx-translate/core';
import { MatDialog } from '@angular/material/dialog';
import { DataProtectionPolicyModalComponent } from '../../shared/dialogs/data-protection-policy-modal/data-protection-policy-modal.component';
import { SessionExpirationModalComponent } from '../../shared/dialogs/session-expiration-modal/session-expiration-modal.component';
import { AppHttpParams } from 'src/app/shared/models/app-http-params';
import { NotificationsService } from './notifications.service';
import {from} from 'rxjs/internal/observable/from';
import { switchMap, tap} from 'rxjs/operators';


@Injectable({
  providedIn: 'root'
})
export class AuthService {

  user: UserView = new User();
  showGdpr$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  userUpdated: EventEmitter<void> = new EventEmitter();
  sessionExpiredTimeout: any;
  private _updatingUserInfo: boolean;
  constructor(private http: HttpClient,
    private router: Router,
    private usersService: UsersService,
    private translateService: TranslateService,
    private matDialog: MatDialog,
    private notificationsService: NotificationsService) {
  }

  isLoggedIn(): boolean {
    return !!this.getAccessTokenResponse() && !this.isRefreshTokenExpired();
  }

  login(login: Login): Observable<void> {
    const params = {};
    const appParams = new AppHttpParams({params, responseLabel: 'login', notifySuccess: false});
  
    return this.http.post<AccessTokenResponse>(
      AppConfigService.apiUrl + ApiUrlConstants.LOGIN, 
      login,
      {params: appParams}
    ).pipe(
      switchMap(accessTokenResponse => {
        this.storeTokens(accessTokenResponse);
        localStorage.setItem('accessToken', JSON.stringify(accessTokenResponse));

        // Fetch secondary permissions asynchronously in background without blocking login navigation
        this.loginAndFetchPermissions(accessTokenResponse).catch(err => {
          console.warn('Background permissions fetch:', err);
        });

        return of(undefined);
      })
    );
  }

  logoutSilently(): void {
    if (this.isLoggedIn()) {
      this.http.post<any>(AppConfigService.apiUrl + ApiUrlConstants.LOGOUT, {}).subscribe();
    }

    this.user = new User();
    this.showGdpr$.next(false);
    this.removeTokens();
  }

  isHelpDesk(): boolean {
    return this.getStoredRole() === RoleEnum.HELP_DESK;
  }

  getUserRole(): string {
    return this.user?.Role || '';
  }

  // getStoredRole(): string {
  //   const storedRole = localStorage.getItem('currentUserRole');
  //     // Handle null, undefined, or "undefined" string
  // if (!storedRole || storedRole === 'undefined' || storedRole === 'null') {
  //   return '';
  // }
  
  // try {
  //   return JSON.parse(storedRole);
  // } catch (error) {
  //   console.error('Error parsing stored role:', error);
  //   return '';
  // }
  //   // return storedRole ? JSON.parse(storedRole) : '';
  // }


  // In auth.service.ts
getStoredRole(): string {
  try {
    const tokenResponseStr = localStorage.getItem(AppConstants.CURRENT_USER_TOKEN);
    if (tokenResponseStr) {
      const tokenResponse = JSON.parse(tokenResponseStr);
      if (tokenResponse && tokenResponse.access_token) {
        const decodedToken: any = jwtDecode(tokenResponse.access_token);
        if (decodedToken && decodedToken.realm_access && decodedToken.realm_access.roles) {
          const roles: string[] = decodedToken.realm_access.roles;
          const matchedRole = roles.find(r => 
            r === 'SUPER_ADMIN' || 
            r === 'HELP_DESK' || 
            r === 'STAMPS_PARTNER' || 
            r === 'ENFORCEMENT_OFFICER' ||
            r === 'STANDARD_USER' ||
            r === 'ISSUER_ADMIN' ||
            r === 'EO_ADMIN' ||
            r === 'EO_USER' ||
            r === 'PRODUCTION_REQUEST'
          );
          if (matchedRole) {
            return matchedRole;
          }
        }
      }
    }
  } catch (e) {
    console.error('Error extracting role from JWT:', e);
  }

  const storedRole = localStorage.getItem('currentUserRole');
  
  if (!storedRole || storedRole === 'undefined' || storedRole === 'null') {
    return '';
  }
  
  try {
    return JSON.parse(storedRole);
  } catch (error) {
    console.error('Invalid role data in localStorage:', error);
    localStorage.removeItem('currentUserRole'); // Clean up invalid data
    return '';
  }
}

// When setting the role
setUserRole(role: string): void {
  if (role && role !== 'undefined') {
    localStorage.setItem('currentUserRole', JSON.stringify(role));
  } else {
    localStorage.removeItem('currentUserRole');
  }
}

  logout(returnUrl?: string, reason?: string): void {
    this.logoutSilently();
    const queryParams: any = {};
    if (returnUrl) {
      queryParams.returnUrl = returnUrl;
    }
    if (reason === 'deactivated') {
      queryParams.deactivated = 'true';
    }
    this.router.navigate([UrlConstants.LOGIN], { queryParams });
  }

  updateUserInfo(): Observable<void> {
    if (this._updatingUserInfo) {
      return of(null);
    }

    this._updatingUserInfo = true;
    return this.usersService.getSelf().pipe(map(userView => {
      this.showGdpr$.next(!userView.GDPR_Accepted);

      if (!userView.GDPR_Accepted) {
        const config = AppConstants.matDialogConfig();
        config.disableClose = true;
        config.data = { closeable: false };
        const dialog = this.matDialog.open(DataProtectionPolicyModalComponent, config);
        dialog.afterClosed().subscribe(accepted => {
          if (accepted) {
            this.usersService.acceptGdpr().subscribe(() => this.showGdpr$.next(false));
          } else {
            this.logout();
          }
        });
      }

      if (userView.Preferred_Language) {
        this.translateService.use(userView.Preferred_Language);
        localStorage.setItem(AppConstants.USER_LOCALE_KEY, userView.Preferred_Language);
      }
      this.setSessionExpiredTimeout();
      this.user = userView;
      localStorage.setItem('currentUserRole', JSON.stringify(userView.Role));
      this.userUpdated.emit();
    }), catchError(() => {
      this.logout();
      return of(null);
    }), finalize(() => {
      this._updatingUserInfo = false;
    }));
  }

  getAuthToken(): Observable<string> {
    if (!this.isLoggedIn()) {
      return throwError(new HttpErrorResponse({ error: 'User is not logged in', status: 401 }));
    }

    if (this.isTokenExpired()) {
      return this.refreshToken();
    }

    return of(this.getAccessTokenResponse().access_token);
  }

  refreshToken(): Observable<string> {
    return this.http.get<AccessTokenResponse>(AppConfigService.apiUrl + ApiUrlConstants.REFRESH, { headers: { 'refresh-token': this.getAccessTokenResponse().refresh_token } })
      .pipe(map(accessTokenResponse => {
        this.storeTokens(accessTokenResponse);
        return accessTokenResponse.access_token;
      })
      );
  }

  getRefreshTokenExpirationDate(): Moment | null {
    return this.isLoggedIn() ? moment(JSON.parse(localStorage.getItem(AppConstants.CURRENT_USER_REFRESH_TOKEN_EXPIRES_AT))) : null;
  }

  setSessionExpiredTimeout() {
    clearTimeout(this.sessionExpiredTimeout);
    
    this.sessionExpiredTimeout = setTimeout(() => {
      const currentUrl = this.router.url;
      if (currentUrl !== UrlConstants.LOGIN) {
        this.matDialog.closeAll();
        
        const dialogRef = this.matDialog.open(SessionExpirationModalComponent, AppConstants.matDialogConfig());

        dialogRef.afterClosed().subscribe((result) => {
          if (result === 'logout') {
            this.logout(this.router.url);
          }
        });
      }
    }, this.getRefreshTokenExpirationDate().diff(moment()));
  }

  private storeTokens(token: AccessTokenResponse): void {
    localStorage.setItem(AppConstants.CURRENT_USER_ROLE, JSON.stringify(this.user.Role));
    localStorage.setItem(AppConstants.CURRENT_USER_TOKEN, JSON.stringify(token));
    localStorage.setItem(AppConstants.CURRENT_USER_TOKEN_EXPIRES_AT, JSON.stringify(moment().add(token.expires_in, 'seconds').valueOf()));
    localStorage.setItem(AppConstants.CURRENT_USER_REFRESH_TOKEN_EXPIRES_AT, JSON.stringify(moment().add(token.refresh_expires_in, 'seconds').valueOf()));
    this.userUpdated.emit();
  }

  private removeTokens(): void {
    localStorage.removeItem(AppConstants.CURRENT_USER_ROLE);
    localStorage.removeItem(AppConstants.CURRENT_USER_TOKEN);
    localStorage.removeItem(AppConstants.CURRENT_USER_REFRESH_TOKEN_EXPIRES_AT);
    localStorage.removeItem(AppConstants.CURRENT_USER_TOKEN_EXPIRES_AT);
    this.userUpdated.emit();
  }

  private isTokenExpired(): boolean {
    return moment().isAfter(JSON.parse(localStorage.getItem(AppConstants.CURRENT_USER_TOKEN_EXPIRES_AT)));
  }

  private isRefreshTokenExpired(): boolean {
    return moment().isAfter(JSON.parse(localStorage.getItem(AppConstants.CURRENT_USER_REFRESH_TOKEN_EXPIRES_AT)));
  }

  private getAccessTokenResponse(): AccessTokenResponse {
    return JSON.parse(localStorage.getItem(AppConstants.CURRENT_USER_TOKEN));
  }

  isSuperAdmin(): boolean {
    return this.getStoredRole() === RoleEnum.SUPER_ADMIN;
  }

  isStandardUser(): boolean {
    return this.getStoredRole() === RoleEnum.STANDARD_USER;
  }

  isReadOnly(): boolean {
    return this.getStoredRole() === RoleEnum.READ_ONLY;
  }

  isIssuerAdmin(): boolean {
    return this.getStoredRole() === RoleEnum.ISSUER_ADMIN;
  }

  isIssuerAdminOrSuperAdmin(): boolean {
    return this.isIssuerAdmin() || this.isSuperAdmin();
  }

  isEoAdmin(): boolean {
    return this.getStoredRole() === RoleEnum.EO_ADMIN;
  }

  isEoUser(): boolean {
    return this.getStoredRole() === RoleEnum.EO_USER;
  }

  isStampsPartner(): boolean {
    const role = this.getStoredRole();
    if (!role) return false;
    const r = role.toUpperCase();
    return r.includes('PARTNER') || r.includes('STAMP') || r === 'STAMPS_PARTNER';
  }

  isAdminOrHelpDesk(): boolean {
    return this.isIssuerAdminOrSuperAdmin() || this.getStoredRole() === RoleEnum.HELP_DESK;
  }

  isProductionRequestUser(): boolean {
    return this.getStoredRole() === RoleEnum.PRODUCTION_REQUEST;
  }

  isEnforcementOfficer(): boolean {
    return this.getStoredRole() === RoleEnum.ENFORCEMENT_OFFICER;
  }

  hasPermission(permission: string): boolean {
    const permissionsItem = localStorage.getItem('userPermissions');
    if (permissionsItem) {
      try {
        const userPermissions = JSON.parse(permissionsItem);
        return userPermissions.permissions && userPermissions.permissions.includes(permission);
      } catch (e) {
        console.error('Error parsing user permissions from localStorage', e);
        return false;
      }
    }
    return false;
  }

  async loginAndFetchPermissions(accessTokenResponse: AccessTokenResponse): Promise<void> {
    try {
      let keycloak_id: string = null;
      try {
        const decodedToken: any = jwtDecode(accessTokenResponse.access_token);
        keycloak_id = decodedToken?.sub;
      } catch (jwtErr) {
        console.warn('Failed to decode JWT with jwtDecode, attempting manual base64 decode:', jwtErr);
        try {
          const payload = accessTokenResponse.access_token.split('.')[1];
          const decoded = JSON.parse(atob(payload));
          keycloak_id = decoded?.sub;
        } catch (b64Err) {
          console.error('Failed to parse JWT payload manually:', b64Err);
        }
      }

      if (keycloak_id) {
        localStorage.setItem('loggedInKeycloakId', keycloak_id);
        const permissionsResponse = await this.fetchUserPermissions(keycloak_id, accessTokenResponse.access_token);
        localStorage.setItem('userPermissions', JSON.stringify(permissionsResponse));
      }
    } catch (error) {
      console.warn('Non-fatal error in loginAndFetchPermissions:', error);
    }
  }

  getUserId(): string | null {
    try {
      // Primary: Get from localStorage
      const keycloakId = localStorage.getItem('loggedInKeycloakId');
      if (keycloakId) {
        return keycloakId;
      }

      // Secondary: Try to extract from token
      const tokenResponse = this.getAccessTokenResponse();
      if (tokenResponse && tokenResponse.access_token) {
        const decodedToken: any = jwtDecode(tokenResponse.access_token);
        if (decodedToken && decodedToken.sub) {
          localStorage.setItem('loggedInKeycloakId', decodedToken.sub);
          return decodedToken.sub;
        }
      }
    } catch (error) {
      console.error('Error getting user ID:', error);
    }
    return null;
  }
  
  async fetchUserPermissions(userId: string, accessToken: string): Promise<{ permissions: string[], entitiesAccess?: string[] }> {
    try {
      const url = AppConfigService.userRoleUrl;
      if (!url || url.includes('undefined')) {
        return { permissions: [] };
      }

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 2000);

      const response = await fetch(`${url}/${userId}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        },
        signal: controller.signal
      });
      clearTimeout(timeoutId);
  
      if (!response.ok) {
        console.warn('Failed to fetch user permissions from entity manager, status:', response.status);
        return { permissions: [] };
      }
  
      const text = await response.text();
      return text && text.trim().length > 0 ? JSON.parse(text) : { permissions: [] };
    } catch (error) {
      console.warn('Error fetching user permissions, defaulting to empty:', error);
      return { permissions: [] };
    }
  }
}
